/**
 * The chat's document tabs that live only in the client, and their transitions.
 *
 * Which tab is shown is not held here: the URL's `?canvas=` names it, so this
 * state can never disagree with it.
 */
import {
  PENDING_KEY_PREFIX,
  isPendingKey,
  type MountedPanel,
  type PendingEditorTarget,
  type SavedEditorTarget,
  type ShownTab
} from '../components/editorPanelTypes';
import { UNTITLED_DOCUMENT_TITLE } from '../constants';

type SavedPanel = Extract<MountedPanel, { readonly target: SavedEditorTarget }>;
export type PendingPanel = Extract<MountedPanel, { readonly target: PendingEditorTarget }>;

/** Which tabs are open, and in what order the bar shows them. */
export interface TabArrangement {
  /** Every open tab's key, in bar order; a key may name a document not listed yet. */
  readonly order: readonly string[];
  /** The canvases whose tabs the user closed. */
  readonly closed: readonly string[];
}

const NO_ARRANGEMENT: TabArrangement = { order: [], closed: [] };

export interface CanvasTabsState {
  /** The chat these tabs belong to. */
  readonly sessionId: string | undefined;
  readonly canvasPanels: readonly SavedPanel[];
  /** The chat's Pending Canvases, oldest first. Each panel's key is its tab's. */
  readonly pendings: readonly PendingPanel[];
  /** The tab shown when the panel was last closed, to reopen on. */
  readonly lastShown: ShownTab | null;
  /** How many Pending Canvases have been opened, so each gets a key and an editor of its own. */
  readonly pendingSerial: number;
  readonly arrangement: TabArrangement;
}

export type CanvasTabsAction =
  /** Enter a chat: its tabs are kept if they already belong to it, else replaced by those remembered for it. */
  | {
      readonly type: 'enterSession';
      readonly sessionId: string | undefined;
      readonly remembered: TabArrangement | null;
    }
  /** Drop every document editor, as leaving the chat does. */
  | { readonly type: 'abandon' }
  /** Keep the tabs of a chat whose first message or document created its session. */
  | { readonly type: 'adoptSession'; readonly sessionId: string }
  /**
   * Bring the arrangement up to date with the chat's canvases, `listedIds`: a new one's tab
   * opens at the end. Once `isComplete`, as when the list has loaded, canvases no longer
   * listed are forgotten, and if every canvas is closed with no Pending Canvas, all reopen.
   */
  | { readonly type: 'syncCanvases'; readonly listedIds: readonly string[]; readonly isComplete: boolean }
  /** Mount a saved canvas's editor, if it is not mounted yet. */
  | { readonly type: 'mountCanvas'; readonly sessionId: string; readonly canvasId: string }
  /** Open a new blank Pending Canvas, its tab at the end. */
  | { readonly type: 'openPending' }
  | { readonly type: 'closeTab'; readonly canvasId: string }
  /** Reopen a canvas's closed tab at the end; an open one moves nowhere. */
  | { readonly type: 'reopenTab'; readonly canvasId: string }
  /** Put the tabs `keys` names first, in that order. */
  | { readonly type: 'reorderTabs'; readonly keys: readonly string[] }
  | { readonly type: 'retitlePending'; readonly key: string; readonly title: string }
  | { readonly type: 'discardPending'; readonly key: string }
  /** A Pending Canvas's first save made it a Canvas; its editor carries on as the Canvas's. */
  | { readonly type: 'pendingSaved'; readonly key: string; readonly sessionId: string; readonly canvasId: string }
  | { readonly type: 'closePanel'; readonly shown: ShownTab | null }
  /** The editors were unmounted with the pane; the Pending Canvases survive only when kept. */
  | { readonly type: 'unmountEditors'; readonly keepPendings: boolean };

/** Return a chat's state before any of its documents is shown, arranged as `remembered` if given. */
export const emptyCanvasTabs = (
  sessionId: string | undefined,
  pendingSerial = 0,
  remembered: TabArrangement | null = null
): CanvasTabsState => ({
  sessionId,
  canvasPanels: [],
  pendings: [],
  lastShown: null,
  pendingSerial,
  arrangement: remembered ?? NO_ARRANGEMENT
});

/** Return the arrangement to remember across reloads: Pending Canvases never are. */
export const rememberedArrangement = ({ order, closed }: TabArrangement): TabArrangement => ({
  order: order.filter((key) => !isPendingKey(key)),
  closed
});

/** Return the key the next Pending Canvas opened will have. */
export const nextPendingKey = (state: CanvasTabsState): string => `${PENDING_KEY_PREFIX}${state.pendingSerial + 1}`;

const findCanvasPanel = (state: CanvasTabsState, canvasId: string) =>
  state.canvasPanels.find(({ target }) => target.canvasId === canvasId);

/** Return the chat's Pending Canvas with `key`, if it still has it. */
export const findPending = (state: CanvasTabsState, key: string): PendingPanel | undefined =>
  state.pendings.find((pending) => pending.key === key);

/** Return the key of the mounted editor showing `tab`, or null if it has none yet. */
export function shownPanelKey(state: CanvasTabsState, tab: ShownTab): string | null {
  switch (tab.kind) {
    case 'saved':
      return findCanvasPanel(state, tab.canvasId)?.key ?? null;
    case 'pending':
      return findPending(state, tab.key)?.key ?? null;
  }
}

const without = (keys: readonly string[], key: string) => keys.filter((candidate) => candidate !== key);

const withoutPending = (state: CanvasTabsState, key: string) => state.pendings.filter((pending) => pending.key !== key);

const sameKeys = (a: readonly string[], b: readonly string[]) =>
  a.length === b.length && a.every((key, index) => key === b[index]);

/** Return `state` arranged as `next`, or `state` itself when that changes nothing, so an idle sync re-renders nothing. */
function arranged(state: CanvasTabsState, next: TabArrangement): CanvasTabsState {
  const { order, closed } = state.arrangement;
  return sameKeys(order, next.order) && sameKeys(closed, next.closed) ? state : { ...state, arrangement: next };
}

function syncCanvases(state: CanvasTabsState, listedIds: readonly string[], isComplete: boolean): CanvasTabsState {
  const { order, closed } = state.arrangement;
  const listed = new Set(listedIds);
  const isKnown = (key: string) => isPendingKey(key) || listed.has(key);
  const keptOrder = isComplete ? order.filter(isKnown) : order;
  const keptClosed = isComplete ? closed.filter((id) => listed.has(id)) : closed;
  const allClosed =
    isComplete &&
    state.pendings.length === 0 &&
    listedIds.length > 0 &&
    listedIds.every((id) => keptClosed.includes(id));
  const nextClosed = allClosed ? [] : keptClosed;
  const placed = new Set(keptOrder);
  const added = listedIds.filter((id) => !placed.has(id) && !nextClosed.includes(id));
  return arranged(state, { order: [...keptOrder, ...added], closed: nextClosed });
}

export function canvasTabsReducer(state: CanvasTabsState, action: CanvasTabsAction): CanvasTabsState {
  const { order, closed } = state.arrangement;
  switch (action.type) {
    case 'enterSession':
      return state.sessionId === action.sessionId
        ? state
        : emptyCanvasTabs(action.sessionId, state.pendingSerial, action.remembered);
    case 'abandon':
      // The chat's arrangement outlives its editors, as it outlives a reload.
      return emptyCanvasTabs(state.sessionId, state.pendingSerial, rememberedArrangement(state.arrangement));
    case 'adoptSession':
      if (state.sessionId !== undefined) return state;
      return {
        ...state,
        sessionId: action.sessionId,
        pendings: state.pendings.map((pending) => ({
          ...pending,
          target: { kind: 'pending', sessionId: action.sessionId }
        }))
      };
    case 'syncCanvases':
      return syncCanvases(state, action.listedIds, action.isComplete);
    case 'mountCanvas':
      if (findCanvasPanel(state, action.canvasId)) return state;
      return {
        ...state,
        canvasPanels: [
          ...state.canvasPanels,
          { key: action.canvasId, target: { kind: 'saved', sessionId: action.sessionId, canvasId: action.canvasId } }
        ]
      };
    case 'openPending': {
      const key = nextPendingKey(state);
      return {
        ...state,
        pendingSerial: state.pendingSerial + 1,
        pendings: [
          ...state.pendings,
          { key, target: { kind: 'pending', sessionId: state.sessionId }, pendingTitle: UNTITLED_DOCUMENT_TITLE }
        ],
        arrangement: { order: [...order, key], closed }
      };
    }
    case 'closeTab':
      return arranged(state, {
        order: without(order, action.canvasId),
        closed: [...without(closed, action.canvasId), action.canvasId]
      });
    case 'reopenTab':
      if (!closed.includes(action.canvasId)) return state;
      return arranged(state, {
        order: [...without(order, action.canvasId), action.canvasId],
        closed: without(closed, action.canvasId)
      });
    case 'reorderTabs':
      return arranged(state, {
        order: [...action.keys, ...order.filter((key) => !action.keys.includes(key))],
        closed
      });
    case 'retitlePending':
      return {
        ...state,
        pendings: state.pendings.map((pending) =>
          pending.key === action.key ? { ...pending, pendingTitle: action.title } : pending
        )
      };
    case 'discardPending':
      return {
        ...state,
        pendings: withoutPending(state, action.key),
        arrangement: { order: without(order, action.key), closed },
        lastShown: state.lastShown?.kind === 'pending' && state.lastShown.key === action.key ? null : state.lastShown
      };
    case 'pendingSaved':
      if (!findPending(state, action.key)) return state;
      return {
        ...state,
        sessionId: action.sessionId,
        canvasPanels: [
          ...state.canvasPanels,
          { key: action.key, target: { kind: 'saved', sessionId: action.sessionId, canvasId: action.canvasId } }
        ],
        // The session the first save created is the others' too, or their saves would each create one.
        pendings: withoutPending(state, action.key).map((pending) => ({
          ...pending,
          target: { kind: 'pending', sessionId: action.sessionId }
        })),
        // Its tab keeps its place on the bar.
        arrangement: { order: order.map((key) => (key === action.key ? action.canvasId : key)), closed }
      };
    case 'closePanel':
      return { ...state, lastShown: action.shown ?? state.lastShown };
    case 'unmountEditors':
      return action.keepPendings
        ? { ...state, canvasPanels: [] }
        : {
            ...state,
            canvasPanels: [],
            pendings: [],
            arrangement: rememberedArrangement(state.arrangement)
          };
  }
}
